import React, { useState, useEffect } from "react";
import styles from "./adminUsers.module.css";
import axios from "axios";
import AdminUsersSearchResults from "../adminUsersSearchResults/AdminUsersSearchResults";
import { IoMdCheckmark } from "react-icons/io";

const apiUrl = process.env.REACT_APP_URL;

const AdminUsers = ({ currUsername }) => {
  /**
   * This component allows the website administrator to search for users and manage them, as well as to send a message to all users. The component uses the AdminUsersSearchResults component to allow the admin to send a message to a single user,reset a user's password, and block / unblock a user from the website.
   * props:
   * currUsername - string - The name of the logged in admin
   *
   */

  const [sendMessageToAllOn, setSendMessageToAllOn] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [messageSuccess, setMessageSuccess] = useState(false);
  const [messageError, setMessageError] = useState("");

  const [maxChars, setMaxChars] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchStatus, setSearchStatus] = useState(null);
  const [foundUsernames, setFoundUsernames] = useState([]);

  /**
   * Get the maximum length of a text message . This data is used in case the admin wants to send a message to all website users.
   */
  useEffect(() => {
    const fetchData = async () => {
      const settingsRes = await axios.get(`${apiUrl}/admin/settings`, {
        withCredentials: true,
        params: {},
      });

      if (settingsRes.data && settingsRes.data.success) {
        const fetchedSettings = settingsRes.data.settings[0];
        setMaxChars(fetchedSettings.max_text_message_length);
      }
    };
    fetchData();
  }, []);

  /**
   * Handle checking / unchecking the checkbox which is used to enable / disable sending a message to all website users.
   */
  const handleCheckboxChange = () => {
    if (sendMessageToAllOn) {
      setMessageSuccess(false);
      setMessageError("");
    }

    setSendMessageToAllOn(!sendMessageToAllOn);
  };

  // Handle change in the text area field of the message to all users
  const handleTextAreaChange = (e) => {
    setMessageText(e.target.value);
  };

  /* Validate the message and if valid - send it to all website users */
  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (messageText === "") {
      setMessageError("The message cannot be empty.");
      return;
    }

    if (messageText.length > maxChars) {
      setMessageError(`The message cannot exceed ${maxChars} characters.`);
      return;
    }

    const messageRes = await axios.post(
      `${apiUrl}/messages/new/all`,
      {
        textContent: messageText,
      },
      {
        withCredentials: true,
      }
    );

    if (messageRes.data && messageRes.data.success) {
      setMessageSuccess(true);
      setSendMessageToAllOn(false);
    }

    setMessageError("");
  };

  /* Handle change in a the username search input field */
  const handleSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
  };

  /* Handle the username search form submission - validate the search query, and if valid - search for the username on the database. The results are displayed by the AdminUsersSearchResults component. */
  const handleSearchFormSubmit = async (e) => {
    e.preventDefault();

    try {
      if (searchTerm.length === 0) {
        setSearchStatus("emptySearch");
        return;
      }

      const response = await axios.get(`${apiUrl}/admin/users/${searchTerm}`, {
        withCredentials: true,
      });

      if (response.status === 200) {
        if (response.data.users.length > 0) {
          setSearchStatus("success");
          setFoundUsernames(response.data.users);
        } else {
          setSearchStatus("notFound");
        }
      }
    } catch (e) {
      console.error("Error - could not obtain the requested information", e);
      setSearchStatus("error");
    }
  };

  return (
    <div className={styles.adminUsers}>
      <div>
        <p className={styles.formTopic}>Messages</p>
        <form className={styles.msgToAllForm}>
          <input type="checkbox" onChange={handleCheckboxChange}></input>
          <label>Send a message to all users</label>
          <textarea
            rows="4"
            cols="50"
            placeholder="Type your message here"
            disabled={!sendMessageToAllOn}
            onChange={handleTextAreaChange}
          ></textarea>
          <button
            className={styles.btnContainer}
            onClick={handleSendMessage}
            disabled={!sendMessageToAllOn}
          >
            Send
          </button>
          {messageError !== "" && (
            <p className={styles.msgError}>{messageError}</p>
          )}
          {messageSuccess && (
            <p className={styles.msgSuccess}>
              <IoMdCheckmark />
              <span>The message was sent successfully.</span>
            </p>
          )}
        </form>
      </div>
      <form onSubmit={handleSearchFormSubmit}>
        <p className={styles.formTopic}>Search Users</p>
        <div className={styles.formGroup}>
          <input
            type="text"
            placeholder=" Search for a username:"
            onChange={handleSearchTermChange}
          ></input>
          <button className={styles.btnContainer}>Search</button>
        </div>
      </form>

      {searchStatus === "success" && (
        <AdminUsersSearchResults
          currUsername={currUsername}
          users={foundUsernames}
        />
      )}
      {searchStatus === "notFound" && (
        <p>The requested username has not been found.</p>
      )}

      {searchStatus === "emptySearch" && (
        <p>Please enter at least one character.</p>
      )}
    </div>
  );
};

export default AdminUsers;
